import Divider from '@rio-cloud/rio-uikit/components/divider/Divider';
import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import Button from '@rio-cloud/rio-uikit/Button';
import { NoServiceVehiclesCallout } from './callouts/NoServiceVehiclesCallout';
import { ExpiringCardsCallout } from './callouts/ExpiringCardsCallout';
import { CostAnalyticsCallout } from './callouts/CostAnalyticsCallout';
import { useGetVehiclesV2Query } from '../../../../codegen/vehicles';
import { isPaymentMethodExpiringWithinMonths } from '../../../../utils/dates';
import { useDispatch } from 'react-redux';
import {
  setCardExpirationFilter,
  setServiceFilters,
} from '../../../onboarding/state/onboardingFilterSlice';
import {
  CardExpirationFilter,
  ServiceFilterType,
} from '../../../onboarding/components/table/VehicleTable';
import { useNavigate } from 'react-router-dom';
import { DASHBOARD_ROUTE, ONBOARDING_ROUTE } from '../../../../routes/routes';
import { setPeriod } from '../../../../state/transactions/transactionFilterSlice';
import { getDatesForPeriod, Period } from '../../../../usePeriodSelectOptions';
import { useTransactions } from '../../../../state/transactions/useTransactions';
import moment from 'moment';
import { UnsubscribedVehiclesCallout } from './callouts/UnsubscribedVehiclesCallout';
import { openMarketplaceCheckoutPopup } from '../../../../utils/popups';
import { useGetAssetsQuery } from '../../../../state/assets/assetsSlice';
import {
  getDismissedCallouts,
  saveDismissedCallouts,
} from './dismissedCalloutsStorage';

enum CategoryKey {
  Overdue = 'overdue',
  ToDo = 'todo',
  Insights = 'insights',
}

type CategoryProps = {
  titleText: JSX.Element;
  noItemsText: JSX.Element;
  callouts: CalloutKeyEnum[];
};

export enum CalloutKeyEnum {
  // vehicles in the fleet with no active services
  NoServiceVehicles = 'noServiceVehicles',
  // cards expiring within the next 2 months
  ExpiringCards = 'expiringCards',
  // last month had transactions, so cost analytics is worth showing
  CostAnalytics = 'costAnalytics',
  // fleet vehicles that have no SimplePay sub
  UnsubscribedVehicles = 'unsubscribedVehicles',
}

const insightCategories: Record<CategoryKey, CategoryProps> = {
  [CategoryKey.Overdue]: {
    titleText: <FormattedMessage id="overview.yourSimplePay.overdue.title" />,
    noItemsText: (
      <FormattedMessage id="overview.yourSimplePay.overdue.noItems" />
    ),
    callouts: [
      CalloutKeyEnum.NoServiceVehicles,
      CalloutKeyEnum.UnsubscribedVehicles,
    ],
  },
  [CategoryKey.ToDo]: {
    titleText: <FormattedMessage id="overview.yourSimplePay.todo.title" />,
    noItemsText: <FormattedMessage id="overview.yourSimplePay.todo.noItems" />,
    callouts: [CalloutKeyEnum.ExpiringCards],
  },
  [CategoryKey.Insights]: {
    titleText: <FormattedMessage id="overview.yourSimplePay.insights.title" />,
    noItemsText: (
      <FormattedMessage id="overview.yourSimplePay.insights.noItems" />
    ),
    callouts: [CalloutKeyEnum.CostAnalytics],
  },
};

export const YourInsights = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // rio assets data
  const { data: rioAssetsQueryData, isLoading: isRioAssetsQueryLoading } =
    useGetAssetsQuery({ status: 'active', type: 'truck' });
  const rioAssetIds = rioAssetsQueryData?.items
    ? rioAssetsQueryData.items.map((item) => item.id)
    : [];

  // vehicle data
  const { data: vehiclesQueryData, isLoading: isVehiclesQueryLoading } =
    useGetVehiclesV2Query();
  const vehicles = vehiclesQueryData?.items || [];

  // transaction data
  const dates = getDatesForPeriod(Period.LAST_MONTH);
  const {
    transactions: previousMonthTransactions,
    isLoading: isTransactionsQueryLoading,
  } = useTransactions({
    fromDisplayDate: moment(dates.from.startOf('day').valueOf()).format(),
    toDisplayDate: moment(dates.to.endOf('day').valueOf()).format(),
  });

  const isLoading =
    isVehiclesQueryLoading ||
    isTransactionsQueryLoading ||
    isRioAssetsQueryLoading;

  const [dismissedCallouts, setDismissedCallouts] =
    useState<CalloutKeyEnum[]>(getDismissedCallouts);

  const dismissCallouts = (callouts: CalloutKeyEnum[]) => {
    setDismissedCallouts(callouts);
    saveDismissedCallouts(callouts);
  };

  let vehiclesWithoutServicesCount = 0;
  const expiredCards: string[] = [];

  const unsubscribedVehiclesCount = rioAssetIds.filter(
    (id) => !vehicles.some((v) => v.id === id),
  ).length;

  // extract data from vehicles for insights
  vehicles.forEach((v) => {
    if (v.serviceActivations.length === 0) {
      vehiclesWithoutServicesCount++;
    }
    v.paymentMethods.forEach((pm) => {
      if (isPaymentMethodExpiringWithinMonths(pm.expiry, 2)) {
        expiredCards.push(`**** ${pm.lastFourDigits}`);
      }
    });
  });

  // to display callouts only if they have data to show
  const calloutsWithData = [
    vehiclesWithoutServicesCount > 0 && CalloutKeyEnum.NoServiceVehicles,
    expiredCards.length > 0 && CalloutKeyEnum.ExpiringCards,
    previousMonthTransactions.length > 0 && CalloutKeyEnum.CostAnalytics,
    unsubscribedVehiclesCount > 0 && CalloutKeyEnum.UnsubscribedVehicles,
  ].filter(Boolean) as CalloutKeyEnum[];

  // display only the callouts the user has not manually dismissed
  const visibleCallouts = calloutsWithData.filter(
    (key) => !dismissedCallouts.includes(key),
  );

  const hideCallout = (key: CalloutKeyEnum) =>
    dismissCallouts([...dismissedCallouts, key]);

  const renderCallout = (key: CalloutKeyEnum) => {
    switch (key) {
      case CalloutKeyEnum.NoServiceVehicles:
        return (
          <NoServiceVehiclesCallout
            vehiclesWithoutServicesCount={vehiclesWithoutServicesCount}
            onClick={() => {
              dispatch(setServiceFilters([ServiceFilterType.NoServices]));
              navigate(ONBOARDING_ROUTE);
            }}
            onClose={() => hideCallout(key)}
          />
        );
      case CalloutKeyEnum.ExpiringCards:
        return (
          <ExpiringCardsCallout
            cardsExpiring={expiredCards}
            onClick={() => {
              dispatch(setCardExpirationFilter(CardExpirationFilter.TwoMonths));
              navigate(ONBOARDING_ROUTE);
            }}
            onClose={() => hideCallout(key)}
          />
        );
      case CalloutKeyEnum.CostAnalytics:
        return (
          <CostAnalyticsCallout
            onClick={() => {
              dispatch(setPeriod(Period.LAST_MONTH));
              navigate(DASHBOARD_ROUTE);
            }}
            onClose={() => hideCallout(key)}
          />
        );
      case CalloutKeyEnum.UnsubscribedVehicles:
        return (
          <UnsubscribedVehiclesCallout
            unsubscribedVehiclesCount={unsubscribedVehiclesCount}
            onClick={openMarketplaceCheckoutPopup}
            onClose={() => hideCallout(key)}
          />
        );
    }
  };

  const categories = Object.entries(insightCategories) as [
    CategoryKey,
    CategoryProps,
  ][];

  return (
    !isLoading && (
      <div className="margin-top-25">
        <div className="display-flex justify-content-between">
          <span className="padding-left-15">
            <FormattedMessage id="overview.yourSimplePay.sectionTitle" />
          </span>
          {visibleCallouts.length > 0 && (
            <Button
              data-testid="your-insights-mark-all-read"
              bsStyle={Button.PRIMARY}
              variant={Button.VARIANT_LINK_INLINE}
              onClick={() => dismissCallouts(Object.values(CalloutKeyEnum))}
            >
              <FormattedMessage id="common.action.markAllAsRead" />
            </Button>
          )}
        </div>
        <Divider />
        <div
          data-testid="your-insights-categories"
          className="display-grid gap-10 grid-cols-1-xs grid-cols-2-sm grid-cols-3-md"
        >
          {categories.map(([categoryKey, categoryProps]) => (
            <CalloutCategory
              key={categoryKey}
              categoryKey={categoryKey}
              categoryProps={categoryProps}
              visibleCategoryCallouts={categoryProps.callouts.filter(
                (calloutKey) => visibleCallouts.includes(calloutKey),
              )}
              renderCallout={renderCallout}
            />
          ))}
        </div>
      </div>
    )
  );
};

/**
 * Renders a category's elements:
 * - a title (with a count badge when more than one callout is available)
 * - either the first visible callout or a placeholder when there's no visible callouts
 */
const CalloutCategory = ({
  categoryProps,
  categoryKey,
  visibleCategoryCallouts,
  renderCallout,
}: {
  categoryProps: CategoryProps;
  categoryKey: CategoryKey;
  visibleCategoryCallouts: CalloutKeyEnum[];
  renderCallout: (key: CalloutKeyEnum) => JSX.Element;
}) => (
  <div
    data-testid={`your-insights-category-${categoryKey}`}
    className="display-flex flex-column height-100pct"
  >
    <div className="text-size-12 margin-5 text-medium">
      {categoryProps.titleText}
      {visibleCategoryCallouts.length > 1 &&
        ` (+${visibleCategoryCallouts.length - 1})`}
    </div>
    {visibleCategoryCallouts.length > 0 ? (
      renderCallout(visibleCategoryCallouts[0])
    ) : (
      <div className="flex-1-1 min-height-90 rounded border text-size-12 padding-10 display-flex flex-column justify-content-center gap-10">
        <div className="text-medium text-center">
          {categoryProps.noItemsText}
        </div>
        <div className="text-center">
          <FormattedMessage id="overview.yourSimplePay.general.noItems" />
        </div>
      </div>
    )}
  </div>
);
